import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { AuthLayout } from '@repo/ui';
import { LoginForm } from './LoginForm';
import { EmailStep } from './forgot-password/EmailStep';
import { OtpStep } from './forgot-password/OtpStep';
import { PasswordStep } from './forgot-password/PasswordStep';
import { SuccessStep } from './forgot-password/SuccessStep';
import { ThemeSwitcher, LanguageSwitcher } from '@repo/settings';
import logo from '@repo/assets/images/logo.png';

export interface AuthPageProps {
  portalName: string;
  heroImage?: string;
  heroTitle1: string;
  heroTitle2: string;
  heroSubtitle: string;
}

type AuthView = 'login' | 'forgot-email' | 'forgot-otp' | 'forgot-password' | 'forgot-success';

export const AuthPage: React.FC<AuthPageProps> = ({
  portalName,
  heroImage,
  heroTitle1,
  heroTitle2,
  heroSubtitle,
}) => {
  const navigate = useNavigate();
  const { t } = useTranslation();

  const [view, setView] = useState<AuthView>('login');
  const [resetEmail, setResetEmail] = useState('');
  const [resetOtp, setResetOtp] = useState('');

  let formHeaderTitle = '';
  let formHeaderSubtitle = '';

  switch (view) {
    case 'login':
      formHeaderTitle = t('auth.signInTitle', { defaultValue: 'Welcome back' });
      formHeaderSubtitle = t('auth.signInSubtitle', {
        defaultValue: 'Sign in to your account to continue',
      });
      break;
    case 'forgot-email':
      formHeaderTitle = t('auth.resetPassword');
      formHeaderSubtitle = t('auth.forgotPasswordSubtitle');
      break;
    case 'forgot-otp':
      formHeaderTitle = t('auth.verifyOtpTitle');
      formHeaderSubtitle = `${t('auth.verifyOtpSubtitle')} ${resetEmail}`;
      break;
    case 'forgot-password':
      formHeaderTitle = t('auth.createNewPasswordTitle');
      formHeaderSubtitle = t('auth.createNewPasswordSubtitle');
      break;
    case 'forgot-success':
      formHeaderTitle = t('auth.passwordResetSuccess');
      formHeaderSubtitle = t('auth.passwordResetSuccessMessage');
      break;
  }

  return (
    <AuthLayout
      logo={logo}
      portalName={portalName}
      heroImage={heroImage}
      heroTitle1={heroTitle1}
      heroTitle2={heroTitle2}
      heroSubtitle={heroSubtitle}
      formHeaderTitle={formHeaderTitle}
      formHeaderSubtitle={formHeaderSubtitle}
      copyrightText={t('marketing.copyright', { defaultValue: '© 2026 Route Labs' })}
      privacyPolicyText={t('common.privacyPolicy', { defaultValue: 'Privacy Policy' })}
      termsOfServiceText={t('common.termsOfService', { defaultValue: 'Terms of Service' })}
      topRightActions={
        <>
          <LanguageSwitcher
            variant="toggle"
            className="bg-background/50 backdrop-blur-sm border border-border/50"
          />
          <ThemeSwitcher
            variant="toggle"
            className="bg-background/50 backdrop-blur-sm border border-border/50"
          />
        </>
      }
    >
      {view === 'login' && (
        <LoginForm
          onSuccess={() => navigate('/')}
          onForgotPassword={() => setView('forgot-email')}
        />
      )}

      {view === 'forgot-email' && (
        <EmailStep
          onBack={() => setView('login')}
          onSuccess={(email) => {
            setResetEmail(email);
            setView('forgot-otp');
          }}
        />
      )}

      {view === 'forgot-otp' && (
        <OtpStep
          email={resetEmail}
          onBack={() => setView('forgot-email')}
          onSuccess={(otp) => {
            setResetOtp(otp);
            setView('forgot-password');
          }}
        />
      )}

      {view === 'forgot-password' && (
        <PasswordStep
          email={resetEmail}
          otp={resetOtp}
          onBack={() => setView('forgot-otp')}
          onSuccess={() => setView('forgot-success')}
        />
      )}

      {view === 'forgot-success' && (
        <SuccessStep onBack={() => setView('login')} onSuccess={() => setView('login')} />
      )}
    </AuthLayout>
  );
};
